import { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import {
  Sun, User, Phone, MapPin, Zap, CheckCircle2, Hash, Home, Building2, Users, Car,
  Lightbulb, BatteryCharging, HelpCircle, Wrench, FileText, ScanLine, Loader2, ArrowLeft,
  Camera, X,
} from "lucide-react";

const LEAD_CATEGORIES = [
  { value: "residential", label: "Residential (Home)", Icon: Home, epc: true },
  { value: "commercial", label: "Commercial (Business)", Icon: Building2, epc: true },
  { value: "housing_society", label: "Housing Society", Icon: Users, epc: true },
  { value: "ev_charger", label: "EV Charger", Icon: Car, epc: false },
  { value: "street_light", label: "Street Light", Icon: Lightbulb, epc: false },
  { value: "battery_storage", label: "Battery Storage", Icon: BatteryCharging, epc: false },
  { value: "others", label: "Others", Icon: HelpCircle, epc: false },
];

const CATEGORY_INTRO = {
  street_light: "Get a quote for solar street lighting for your municipality, panchayat or gated road — fill in your details and we'll reach out shortly.",
  housing_society: "Get a free quote for shared rooftop solar for your housing society — fill in your details and we'll reach out shortly.",
};

const HEADER = (
  <div className="bg-gradient-to-br from-[#0D4721] via-[#15803D] to-[#0B3C1C] px-6 py-6 flex items-center gap-3">
    <img src="/purvodaya-logo.png" alt="Purvodaya Solar" className="h-10 w-auto" />
    <div className="text-white">
      <div className="font-extrabold text-lg leading-tight">Purvodaya Solar</div>
      <div className="text-[11px] text-amber-200 uppercase tracking-widest">Scan &amp; Connect</div>
    </div>
  </div>
);

/** Reusable "Scan your Electricity Bill" uploader — autofills name/phone/address/pin
 * on whichever form is active, via the unauthenticated /public/ocr-bill endpoint. */
function BillScanner({ onExtracted }) {
  const inputRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [done, setDone] = useState(false);
  const [failMsg, setFailMsg] = useState("");

  async function onSelected(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);
    setDone(false);
    setFailMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/public/ocr-bill", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const ex = data.extracted || {};
      const gotSomething = !ex._error && Object.entries(ex).some(([k, v]) => k !== "_error" && v != null && v !== "");
      if (gotSomething) {
        onExtracted(ex);
        setDone(true);
      } else {
        setFailMsg("Couldn't read the bill clearly — please fill the fields in manually.");
      }
    } catch {
      setFailMsg("Couldn't scan the bill right now — please fill the fields in manually.");
    } finally {
      setScanning(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <div>
      <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onSelected} data-testid="public-bill-input" />
      <button
        type="button" onClick={() => inputRef.current?.click()} disabled={scanning}
        data-testid="public-scan-bill-btn"
        className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-emerald-300 bg-emerald-50 text-emerald-800 rounded-xl py-3 text-sm font-semibold hover:border-emerald-400 transition-colors disabled:opacity-60"
      >
        {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanLine className="w-4 h-4" />}
        {scanning ? "Reading your bill…" : done ? "Bill scanned — details filled below" : "Scan your Electricity Bill to auto-fill"}
      </button>
      {failMsg && <div className="text-xs text-amber-600 mt-1.5" data-testid="public-scan-bill-error">{failMsg}</div>}
    </div>
  );
}

function NewInstallationForm({ scId, initialCategory, onDone, onBack }) {
  const [form, setForm] = useState({ name: "", phone: "", address: "", pin_code: "", lead_category: initialCategory || "residential", monthly_bill: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const isEpc = LEAD_CATEGORIES.find((c) => c.value === form.lead_category)?.epc;

  function applyOcr(ex) {
    setForm((f) => ({
      ...f,
      name: ex.name || f.name,
      phone: ex.phone || f.phone,
      address: ex.address || f.address,
      pin_code: ex.pin_code ? String(ex.pin_code).replace(/\D/g, "").slice(0, 6) : f.pin_code,
    }));
  }

  async function submit(e) {
    e.preventDefault();
    if (!scId) { setError("This link is missing a referral code — please re-scan the flyer QR code."); return; }
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/public/sc-lead", {
        name: form.name.trim(), phone: form.phone.trim(), address: form.address.trim(),
        pin_code: form.pin_code.trim(), lead_category: form.lead_category,
        monthly_bill: isEpc ? (Number(form.monthly_bill) || 0) : 0, sc_id: scId,
      });
      onDone(data.message);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={submit} className="p-6 space-y-4">
      {onBack && (
        <button type="button" onClick={onBack} data-testid="public-new-back" className="flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
      )}
      <p className="text-sm text-slate-600">{CATEGORY_INTRO[form.lead_category] || "Get a free rooftop solar quote — fill in your details and we'll reach out shortly."}</p>
      {!scId && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="public-sc-lead-missing-ref">
          Missing referral code in this link — please scan the flyer's QR code again.
        </div>
      )}
      {form.lead_category !== "street_light" && <BillScanner onExtracted={applyOcr} />}
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">I'm interested in *</label>
        <div className="grid grid-cols-2 gap-2">
          {LEAD_CATEGORIES.map((c) => (
            <button
              type="button" key={c.value} onClick={() => setForm({ ...form, lead_category: c.value })}
              data-testid={`sc-lead-category-${c.value}`}
              className={"flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-xs font-semibold border-2 transition-colors " +
                (form.lead_category === c.value ? "border-emerald-600 bg-emerald-50 text-emerald-800" : "border-slate-200 text-slate-600 hover:border-slate-300")}
            >
              <c.Icon className="w-3.5 h-3.5" /> {c.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Full Name *</label>
        <div className="flex items-center gap-2 border-2 border-slate-200 focus-within:border-emerald-600 rounded-xl px-3 py-2.5">
          <User className="w-4 h-4 text-slate-400" />
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="sc-lead-name" placeholder="Your name" className="flex-1 outline-none text-sm" />
        </div>
      </div>
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Mobile Number *</label>
        <div className="flex items-center gap-2 border-2 border-slate-200 focus-within:border-emerald-600 rounded-xl px-3 py-2.5">
          <Phone className="w-4 h-4 text-slate-400" />
          <input required type="tel" inputMode="numeric" value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
            data-testid="sc-lead-phone" placeholder="98765 43210" className="flex-1 outline-none text-sm font-mono" />
        </div>
      </div>
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Address</label>
        <div className="flex items-center gap-2 border-2 border-slate-200 focus-within:border-emerald-600 rounded-xl px-3 py-2.5">
          <MapPin className="w-4 h-4 text-slate-400" />
          <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
            data-testid="sc-lead-address" placeholder="City / locality" className="flex-1 outline-none text-sm" />
        </div>
      </div>
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">PIN Code *</label>
        <div className="flex items-center gap-2 border-2 border-slate-200 focus-within:border-emerald-600 rounded-xl px-3 py-2.5">
          <Hash className="w-4 h-4 text-slate-400" />
          <input required inputMode="numeric" maxLength={6} value={form.pin_code}
            onChange={(e) => setForm({ ...form, pin_code: e.target.value.replace(/\D/g, "").slice(0, 6) })}
            data-testid="sc-lead-pin" placeholder="560001" className="flex-1 outline-none text-sm font-mono" />
        </div>
      </div>
      {isEpc && (
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Monthly Electricity Bill (₹)</label>
          <div className="flex items-center gap-2 border-2 border-slate-200 focus-within:border-emerald-600 rounded-xl px-3 py-2.5">
            <Zap className="w-4 h-4 text-slate-400" />
            <input type="number" min={0} value={form.monthly_bill} onChange={(e) => setForm({ ...form, monthly_bill: e.target.value })}
              data-testid="sc-lead-bill" placeholder="3500" className="flex-1 outline-none text-sm font-mono" />
          </div>
        </div>
      )}
      {error && <div className="text-xs text-red-600" data-testid="sc-lead-error">{error}</div>}
      <button type="submit" disabled={submitting} data-testid="sc-lead-submit"
        className="w-full bg-gradient-to-br from-[#0D4721] via-[#15803D] to-[#0B3C1C] text-white py-3.5 rounded-xl font-bold uppercase tracking-wider text-sm disabled:opacity-50 flex items-center justify-center gap-2">
        <Sun className="w-4 h-4" /> {submitting ? "Submitting…" : "Get Free Quote"}
      </button>
    </form>
  );
}

function ServiceIssueForm({ scId, onDone, onBack }) {
  const [form, setForm] = useState({ name: "", phone: "", address: "", pin_code: "", title: "", description: "" });
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const photoInputRef = useRef(null);

  function applyOcr(ex) {
    setForm((f) => ({
      ...f,
      name: ex.name || f.name,
      phone: ex.phone || f.phone,
      address: ex.address || f.address,
      pin_code: ex.pin_code ? String(ex.pin_code).replace(/\D/g, "").slice(0, 6) : f.pin_code,
    }));
  }

  function onPickPhoto(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  }

  function removePhoto() {
    setPhoto(null);
    setPhotoPreview(null);
    if (photoInputRef.current) photoInputRef.current.value = "";
  }

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("name", form.name.trim());
      fd.append("phone", form.phone.trim());
      fd.append("address", form.address.trim());
      fd.append("pin_code", form.pin_code.trim());
      fd.append("title", form.title.trim());
      fd.append("description", form.description.trim());
      if (scId) fd.append("sc_id", scId);
      if (photo) fd.append("photo", photo);
      const { data } = await api.post("/public/service-issue", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onDone({ message: data.message, photoError: data.photo_error });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={submit} className="p-6 space-y-4" data-testid="public-issue-form">
      <button type="button" onClick={onBack} data-testid="public-issue-back" className="flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800">
        <ArrowLeft className="w-3.5 h-3.5" /> Back
      </button>
      <p className="text-sm text-slate-600">Tell us what's wrong with your solar system and our service team will reach out.</p>
      <BillScanner onExtracted={applyOcr} />
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Full Name *</label>
        <div className="flex items-center gap-2 border-2 border-slate-200 focus-within:border-emerald-600 rounded-xl px-3 py-2.5">
          <User className="w-4 h-4 text-slate-400" />
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="public-issue-name" placeholder="Your name" className="flex-1 outline-none text-sm" />
        </div>
      </div>
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Mobile Number *</label>
        <div className="flex items-center gap-2 border-2 border-slate-200 focus-within:border-emerald-600 rounded-xl px-3 py-2.5">
          <Phone className="w-4 h-4 text-slate-400" />
          <input required type="tel" inputMode="numeric" value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
            data-testid="public-issue-phone" placeholder="98765 43210" className="flex-1 outline-none text-sm font-mono" />
        </div>
      </div>
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Address</label>
        <div className="flex items-center gap-2 border-2 border-slate-200 focus-within:border-emerald-600 rounded-xl px-3 py-2.5">
          <MapPin className="w-4 h-4 text-slate-400" />
          <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
            data-testid="public-issue-address" placeholder="City / locality" className="flex-1 outline-none text-sm" />
        </div>
      </div>
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">What's the issue? *</label>
        <div className="flex items-center gap-2 border-2 border-slate-200 focus-within:border-emerald-600 rounded-xl px-3 py-2.5">
          <Wrench className="w-4 h-4 text-slate-400" />
          <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            data-testid="public-issue-title" placeholder="e.g. Inverter not turning on" className="flex-1 outline-none text-sm" />
        </div>
      </div>
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">More details</label>
        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
          data-testid="public-issue-description" placeholder="Anything else our team should know?" rows={3}
          className="w-full border-2 border-slate-200 focus:border-emerald-600 rounded-xl px-3 py-2.5 text-sm outline-none resize-none" />
      </div>
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Photo of the fault (optional)</label>
        <input ref={photoInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPickPhoto} data-testid="public-issue-photo-input" />
        {photoPreview ? (
          <div className="relative w-24 h-24" data-testid="public-issue-photo-preview">
            <img src={photoPreview} alt="Fault preview" className="w-24 h-24 object-cover rounded-xl border-2 border-emerald-200" />
            <button type="button" onClick={removePhoto} data-testid="public-issue-photo-remove"
              className="absolute -top-2 -right-2 bg-white border border-slate-300 rounded-full p-1 shadow-sm">
              <X className="w-3.5 h-3.5 text-slate-600" />
            </button>
          </div>
        ) : (
          <button type="button" onClick={() => photoInputRef.current?.click()} data-testid="public-issue-photo-btn"
            className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 bg-slate-50 text-slate-600 rounded-xl py-3 text-sm font-semibold hover:border-emerald-400 transition-colors">
            <Camera className="w-4 h-4" /> Attach a photo
          </button>
        )}
      </div>
      {error && <div className="text-xs text-red-600" data-testid="public-issue-error">{error}</div>}
      <button type="submit" disabled={submitting} data-testid="public-issue-submit"
        className="w-full bg-gradient-to-br from-[#0D4721] via-[#15803D] to-[#0B3C1C] text-white py-3.5 rounded-xl font-bold uppercase tracking-wider text-sm disabled:opacity-50 flex items-center justify-center gap-2">
        <Wrench className="w-4 h-4" /> {submitting ? "Submitting…" : "Report Issue"}
      </button>
    </form>
  );
}

/** Public QR landing page reached by scanning an SC's flyer/QR code. Fully public,
 * no auth required — works standalone on any phone browser. Offers two paths: a new
 * rooftop solar enquiry, or reporting a service issue on an existing installation —
 * both can be speeded up by scanning the customer's electricity bill. */
export default function PublicScLead() {
  const [params] = useSearchParams();
  const scId = params.get("sc") || "";
  const categoryParam = params.get("category") || "";
  const validCategory = LEAD_CATEGORIES.find((c) => c.value === categoryParam)?.value || null;
  const [mode, setMode] = useState(validCategory ? "new" : null); // null | "new" | "issue"
  const [done, setDone] = useState(null);

  return (
    <div className="min-h-screen bg-[#0B3C1C] flex items-center justify-center p-4" data-testid="public-sc-lead-page">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        {HEADER}

        {!mode && !done && (
          <div className="relative h-32 overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1724041875334-0a6397111c7e?crop=entropy&cs=srgb&fm=jpg&q=85"
              alt="Rooftop solar panels" className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          </div>
        )}

        {done ? (
          <div className="p-8 text-center" data-testid="public-sc-lead-success">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            </div>
            <h2 className="text-xl font-extrabold text-slate-900">Thank you!</h2>
            <p className="text-sm text-slate-600 mt-2">{typeof done === "object" ? done.message : done}</p>
            {typeof done === "object" && done.photoError && (
              <p className="text-xs text-amber-600 mt-2" data-testid="public-sc-lead-photo-warning">{done.photoError}</p>
            )}
          </div>
        ) : mode === "new" ? (
          <NewInstallationForm scId={scId} initialCategory={validCategory} onBack={validCategory ? null : () => setMode(null)} onDone={setDone} />
        ) : mode === "issue" ? (
          <ServiceIssueForm scId={scId} onBack={() => setMode(null)} onDone={setDone} />
        ) : (
          <div className="p-6 space-y-3" data-testid="public-sc-lead-mode-select">
            <p className="text-sm text-slate-600 mb-1">What would you like to do?</p>
            <button
              type="button" onClick={() => setMode("new")} data-testid="public-mode-new"
              className="w-full flex items-center gap-3 border-2 border-emerald-200 hover:border-emerald-500 bg-emerald-50 rounded-xl px-4 py-4 text-left transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center flex-shrink-0">
                <Sun className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-sm font-bold text-slate-900">New Solar Installation</div>
                <div className="text-xs text-slate-500">Home, business, society, street light &amp; more</div>
              </div>
            </button>
            <button
              type="button" onClick={() => setMode("issue")} data-testid="public-mode-issue"
              className="w-full flex items-center gap-3 border-2 border-amber-200 hover:border-amber-500 bg-amber-50 rounded-xl px-4 py-4 text-left transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
                <Wrench className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-sm font-bold text-slate-900">Report a Service Issue</div>
                <div className="text-xs text-slate-500">Already have a system installed? Log a complaint</div>
              </div>
            </button>
            {!scId && (
              <div className="flex items-center gap-1.5 text-[11px] text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="public-sc-lead-missing-ref">
                <FileText className="w-3 h-3" /> Missing referral code in this link — please scan the flyer's QR code again.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
